import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { apiDomain } from "configs/configs";
import { logout } from "helpers/helpers";
import {
  Select,
  Flex,
  Input,
  InputNumber,
  Checkbox,
  Form,
  Row,
  Col,
  Typography,
  Button,
  Card,
  Grid,
  notification,
  Upload,
  Modal,
  UploadFile,
  Space,
} from "antd";
import {
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { Modal as MobileModal } from "antd-mobile";
import { UploadChangeParam } from "antd/es/upload/interface";
import { PlusOutlined, MinusOutlined, UploadOutlined } from "@ant-design/icons";
import styles from "pages/admin/objectsAndClients/objects/objects.module.scss";
import clsx from "clsx";
import MobileInnerWrapper from "layouts/mobileInnerWrapper/mobileInnerWrapper";
import Table, { ColumnsType } from "components/table/table";
import { Link, useParams, useNavigate } from "react-router-dom";
import { create } from "zustand";
import AddBuilding from "pages/admin/objectsAndClients/objects/addBuilding/addBuilding";
import AddGroup from "pages/admin/objectsAndClients/objects/addGroup/addGroup";
import AddElement from "pages/admin/objectsAndClients/objects/addElement/addElement";
import AddMaster from "pages/admin/objectsAndClients/objects/addMaster/addMaster";
import Report from "pages/admin/objectsAndClients/objects/report/report";
import {
  StageUnitType,
  ObjectType,
  SurfaceType,
  MeasureGroupType,
  MeasureElementType,
  MeasureType,
  MeasureTableElementType,
  StageType,
  SeniorMasterType,
  MasterType,
} from "pages/admin/objectsAndClients/objects/types";

const { Text } = Typography;

type ComponentProps = {};

const { Title } = Typography;
const { useBreakpoint } = Grid;

type OperationsStoreState = {
  list: { isLoaded: boolean; data: ObjectType[] };
  setList: (list: { isLoaded: boolean; data: ObjectType[] }) => void;
  putIds: number[];
  setPutIds: (update: (state: number[]) => number[]) => void;
  isPostInProcess: boolean;
  setIsPostInProcess: (isPostInProcess: boolean) => void;
  nameFilter: string;
  setNameFilter: (nameFilter: string) => void;
  isFileUploading: boolean;
  setIsFileUploading: (isError: boolean) => void;
  seniorMastersList: { isLoaded: boolean; data: SeniorMasterType[] };
  setSeniorMastersList: (seniorMastersList: {
    isLoaded: boolean;
    data: SeniorMasterType[];
  }) => void;
  mastersList: { isLoaded: boolean; data: MasterType[] };
  setMastersList: (mastersList: {
    isLoaded: boolean;
    data: MasterType[];
  }) => void;
  stageUnitsList: { isLoaded: boolean; data: StageUnitType[] };
  setStageUnitsList: (list: {
    isLoaded: boolean;
    data: StageUnitType[];
  }) => void;
  stagesList: ObjectType["stages"];
  setStagesList: (
    update: (state: ObjectType["stages"]) => ObjectType["stages"]
  ) => void;
  createObjectStagesList: { isLoaded: boolean; data: StageType[] };
  setCreateObjectStagesList: (createObjectStagesList: {
    isLoaded: boolean;
    data: StageType[];
  }) => void;
  surfacesList: { isLoaded: boolean; data: SurfaceType[] };
  setSurfacesList: (surfacesList: {
    isLoaded: boolean;
    data: SurfaceType[];
  }) => void;
  previewFile?: UploadFile;
  setPreviewFile: (previewFile?: UploadFile) => void;
  isSubmittable: boolean;
  setIsSubmittable: (isSubmittable: boolean) => void;
  isError: boolean;
  setIsError: (isError: boolean) => void;

  measures: MeasureType[];
  setMeasures: (update: (state: MeasureType[]) => MeasureType[]) => void;
  masters: MasterType[];
  setMasters: (update: (state: MasterType[]) => MasterType[]) => void;
  isAddBuildingMode: boolean;
  setIsAddBuildingMode: (isAddBuildingMode: boolean) => void;
  isEditBuildingMode: string | undefined;
  setIsEditBuildingMode: (isEditBuildingMode?: string) => void;

  isAddGroupMode: boolean;
  setIsAddGroupMode: (isAddBuildingMode: boolean) => void;
  isEditGroupMode: string | undefined;
  setIsEditGroupMode: (isEditBuildingMode?: string) => void;

  isAddElementMode: boolean;
  setIsAddElementMode: (isAddElementMode: boolean) => void;
  isEditElementMode: string | undefined;
  setIsEditElementMode: (isEditElementMode?: string) => void;

  isAddMasterMode: boolean;
  setIsAddMasterMode: (isAddMasterMode: boolean) => void;
};

const useOperationsStore = create<OperationsStoreState>((set) => ({
  list: {
    isLoaded: false,
    data: [],
  },
  setList: (list) => set({ list }),
  putIds: [],
  setPutIds: (update) =>
    set((state) => ({
      putIds: update(state.putIds),
    })),
  isPostInProcess: false,
  setIsPostInProcess: (isPostInProcess) => set({ isPostInProcess }),
  nameFilter: "",
  setNameFilter: (nameFilter) => set({ nameFilter }),
  isFileUploading: false,
  setIsFileUploading: (isFileUploading) => set({ isFileUploading }),
  seniorMastersList: {
    isLoaded: false,
    data: [],
  },
  setSeniorMastersList: (seniorMastersList) => set({ seniorMastersList }),
  mastersList: {
    isLoaded: false,
    data: [],
  },
  setMastersList: (mastersList) => set({ mastersList }),
  stageUnitsList: {
    isLoaded: false,
    data: [],
  },
  setStageUnitsList: (stageUnitsList) => set({ stageUnitsList }),
  stagesList: [],
  setStagesList: (update) =>
    set((state) => ({
      stagesList: update(state.stagesList),
    })),
  createObjectStagesList: {
    isLoaded: false,
    data: [],
  },
  setCreateObjectStagesList: (createObjectStagesList) =>
    set({ createObjectStagesList }),
  surfacesList: {
    isLoaded: false,
    data: [],
  },
  setSurfacesList: (surfacesList) => set({ surfacesList }),
  previewFile: undefined,
  setPreviewFile: (previewFile?: UploadFile) => set({ previewFile }),
  isSubmittable: false,
  setIsSubmittable: (isSubmittable) => set({ isSubmittable }),
  isError: false,
  setIsError: (isError) => set({ isError }),

  measures: [],
  setMeasures: (update) =>
    set((state) => ({
      measures: update(state.measures),
    })),
  masters: [],
  setMasters: (update) =>
    set((state) => ({
      masters: update(state.masters),
    })),
  isAddBuildingMode: false,
  setIsAddBuildingMode: (isAddBuildingMode) =>
    set({ isAddBuildingMode }, false),
  isEditBuildingMode: undefined,
  setIsEditBuildingMode: (isEditBuildingMode) =>
    set({ isEditBuildingMode }, false),

  isAddGroupMode: false,
  setIsAddGroupMode: (isAddGroupMode) => set({ isAddGroupMode }, false),
  isEditGroupMode: undefined,
  setIsEditGroupMode: (isEditGroupMode) => set({ isEditGroupMode }, false),

  isAddElementMode: false,
  setIsAddElementMode: (isAddElementMode) => set({ isAddElementMode }, false),
  isEditElementMode: undefined,
  setIsEditElementMode: (isEditElementMode) =>
    set({ isEditElementMode }, false),

  isAddMasterMode: false,
  setIsAddMasterMode: (isAddMasterMode) => set({ isAddMasterMode }, false),
}));

const Page: React.FC<ComponentProps> = () => {
  const { "*": subRoute } = useParams();
  const navigate = useNavigate();

  const isAddMode = useMemo(() => {
    if (subRoute === "add") return true;
    if (subRoute?.startsWith("add/")) return true;

    return false;
  }, [subRoute]);

  const isEditMode = useMemo(() => {
    if (!subRoute?.startsWith("edit/")) return false;

    const id = parseInt(subRoute.split("/")[1], 10);

    return isNaN(id) ? false : id;
  }, [subRoute]);

  const tab = useMemo(() => {
    if ((!isEditMode && !isAddMode) || !subRoute) return null;

    const parts = subRoute.split("/");

    if (isEditMode)
      return parts.length > 2 ? parts[parts.length - 1] : "default";

    if (isAddMode)
      return parts.length > 1 ? parts[parts.length - 1] : "default";
  }, [subRoute, isEditMode, isAddMode]);

  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  const {
    list,
    setList,
    putIds,
    setPutIds,
    isPostInProcess,
    setIsPostInProcess,
    nameFilter,
    setNameFilter,
    isFileUploading,
    setIsFileUploading,
    seniorMastersList,
    setSeniorMastersList,
    mastersList,
    setMastersList,
    stageUnitsList,
    setStageUnitsList,
    stagesList,
    setStagesList,
    createObjectStagesList,
    setCreateObjectStagesList,
    surfacesList,
    setSurfacesList,
    previewFile,
    setPreviewFile,
    isSubmittable,
    setIsSubmittable,
    isError,
    setIsError,
    measures,
    setMeasures,
    masters,
    setMasters,
    isAddBuildingMode,
    setIsAddBuildingMode,
    isEditBuildingMode,
    setIsEditBuildingMode,
    isAddGroupMode,
    setIsAddGroupMode,
    isEditGroupMode,
    setIsEditGroupMode,
    isAddElementMode,
    setIsAddElementMode,
    isEditElementMode,
    setIsEditElementMode,
    isAddMasterMode,
    setIsAddMasterMode,
  } = useOperationsStore((store) => store);

  const listRef = useRef(list);
  const seniorMastersListRef = useRef(seniorMastersList);
  const stagesListRef = useRef(stagesList);
  const mastersListRef = useRef(mastersList);
  const stageUnitsListRef = useRef(stageUnitsList);
  const createObjectStagesListRef = useRef(createObjectStagesList);
  const surfacesListRef = useRef(surfacesList);

  //  Error while saving change status
  const openNotificationWithError = useCallback(
    ({ message }: { message: string }) => {
      notification.error({
        message: "Ошибка",
        description: message,
        placement: "topRight",
        duration: 3,
      });
    },
    []
  );

  //  Success message
  const openNotificationWithSuccess = useCallback(
    ({ message }: { message: string }) => {
      notification.success({
        message: "Сохранено",
        description: message,
        placement: "topRight",
        duration: 3,
      });
    },
    []
  );

  //  Save current list to the ref so we can use it in useEffect without trigger it
  useEffect(() => {
    listRef.current = list;
  }, [list]);

  useEffect(() => {
    seniorMastersListRef.current = seniorMastersList;
  }, [seniorMastersList]);

  useEffect(() => {
    mastersListRef.current = mastersList;
  }, [mastersList]);

  useEffect(() => {
    stageUnitsListRef.current = stageUnitsList;
  }, [stageUnitsList]);

  useEffect(() => {
    createObjectStagesListRef.current = createObjectStagesList;
  }, [createObjectStagesList]);

  useEffect(() => {
    surfacesListRef.current = surfacesList;
  }, [surfacesList]);

  //  Save current stagesList to the ref so we can use it in useEffect without trigger it
  useEffect(() => {
    stagesListRef.current = stagesList;
  }, [stagesList]);

  //  Effect triggers when mode changes to edit or add. Also when some value changes. It fires validation and reset error.
  useEffect(() => {
    if (!isAddMode && !isEditMode) return;

    setIsError(false);

    form
      .validateFields({ validateOnly: true })
      .then(() => {
        setIsSubmittable(true);
      })
      .catch((reason) => {
        setIsSubmittable(false);
      });
  }, [isAddMode, isEditMode, form, values, setIsError, setIsSubmittable]);

  //  Get all objects
  const getList = useCallback(
    async (isRefresh?: boolean) => {
      const fetchData = async () => {
        try {
          const url = `${apiDomain}/api/records/objects`;
          const options: RequestInit = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          };

          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          const data = await response.json();

          if (!data?.records) throw new Error();

          const records: ObjectType[] = data.records;

          setList({ isLoaded: true, data: records });
        } catch (error) {
          logout();
        }
      };

      if (listRef.current.isLoaded && !isRefresh) return;

      await fetchData();
    },
    [setList]
  );

  //  Get all stages
  const getCreateObjectStagesList = useCallback(
    async (isRefresh?: boolean) => {
      const fetchData = async () => {
        try {
          const url = `${apiDomain}/api/records/stages?order=sort_order,order=id`;
          const options: RequestInit = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          };

          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          const data = await response.json();

          if (!data?.records) throw new Error();

          const records: StageType[] = data.records;

          setCreateObjectStagesList({ isLoaded: true, data: records });
        } catch (error) {
          logout();
        }
      };

      if (createObjectStagesListRef.current.isLoaded && !isRefresh) return;

      await fetchData();
    },
    [setCreateObjectStagesList]
  );

  const getSurfacesList = useCallback(
    async (isRefresh?: boolean) => {
      const fetchData = async () => {
        try {
          const url = `${apiDomain}/api/records/surfaces?filter=status,eq,active`;
          const options: RequestInit = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          };

          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          const data = await response.json();

          if (!data?.records) throw new Error();

          const records: SurfaceType[] = data.records;

          setSurfacesList({ isLoaded: true, data: records });
        } catch (error) {
          logout();
        }
      };

      if (surfacesListRef.current.isLoaded && !isRefresh) return;

      await fetchData();
    },
    [setSurfacesList]
  );

  //  Get all senior masters
  const getSeniorMastersList = useCallback(
    async (isRefresh?: boolean) => {
      const fetchData = async () => {
        try {
          const url = `${apiDomain}/api/records/users?filter=role,eq,senior_master&filter=status,eq,active`;
          const options: RequestInit = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          };

          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          const data = await response.json();

          if (!data?.records) throw new Error();

          const records: SeniorMasterType[] = data.records;

          setSeniorMastersList({ isLoaded: true, data: records });
        } catch (error) {
          logout();
        }
      };

      if (seniorMastersListRef.current.isLoaded && !isRefresh) return;

      await fetchData();
    },
    [setSeniorMastersList]
  );

  //  Get all masters
  const getMastersList = useCallback(
    async (isRefresh?: boolean) => {
      const fetchData = async () => {
        try {
          const url = `${apiDomain}/api/records/users?filter=role,eq,master&filter=status,eq,active`;
          const options: RequestInit = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          };

          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          const data = await response.json();

          if (!data?.records) throw new Error();

          const records: MasterType[] = data.records;

          setMastersList({ isLoaded: true, data: records });
        } catch (error) {
          logout();
        }
      };

      if (mastersListRef.current.isLoaded && !isRefresh) return;

      await fetchData();
    },
    [setMastersList]
  );

  //    Get all stage units
  const getStageUnitsList = useCallback(
    async (isRefresh?: boolean) => {
      const fetchData = async () => {
        try {
          const url = `${apiDomain}/api/records/stage_units?filter=status,eq,active`;
          const options: RequestInit = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          };

          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          const data = await response.json();

          if (!data?.records) throw new Error();

          const records: SeniorMasterType[] = data.records;

          setStageUnitsList({ isLoaded: true, data: records });
        } catch (error) {
          logout();
        }
      };

      if (stageUnitsListRef.current.isLoaded && !isRefresh) return;

      await fetchData();
    },
    [setStageUnitsList]
  );

  //  When started, get data
  useEffect(() => {
    getList();
    getSeniorMastersList();
    getMastersList();
    getStageUnitsList();
    getCreateObjectStagesList();
    getSurfacesList();

    return () => {
      //  When leave page reset all
      setList({ isLoaded: false, data: [] });
      setSeniorMastersList({ isLoaded: false, data: [] });
      setMastersList({ isLoaded: false, data: [] });
      setStageUnitsList({ isLoaded: false, data: [] });
      setCreateObjectStagesList({ isLoaded: false, data: [] });
      setSurfacesList({ isLoaded: false, data: [] });
    };
  }, [
    getList,
    getSeniorMastersList,
    getMastersList,
    getStageUnitsList,
    getCreateObjectStagesList,
    getSurfacesList,
    setList,
    setSeniorMastersList,
    setMastersList,
    setStageUnitsList,
    setCreateObjectStagesList,
    setSurfacesList,
  ]);

  //  Edit button sets edit mode or reset it
  const handleEditButton = useCallback(
    (id?: number) => {
      navigate(`/objects-and-clients/edit/${id}`);
    },
    [navigate]
  );

  //  Triggers when isEditMode changed. Set initial fields value in form or reset it. Reset error.
  useEffect(() => {
    if (!createObjectStagesList.isLoaded) return;
    if (!mastersList.isLoaded) return;

    const item = listRef.current.data.find((item) => item.id === isEditMode);

    if (!item) {
      form.resetFields();

      setStagesList(() =>
        createObjectStagesList.data
          .filter((stage) => stage.status === "active")
          .map((stage) => ({
            ...stage,
            isEnabled: stage.status === "active",
            short_name: "",
            rate_code: "",
            status: "unactive",
            units: stage.units?.map((unit) => ({
              ...unit,
              value: null,
            })),
          }))
      );

      setMeasures(() => []);

      setMasters(() => []);
    }

    if (item) {
      form.setFieldsValue({
        name: item.name,
        address: item.address,
        area: item.area,
        status: item.status,
        senior_masters: item.senior_masters,
        masters: item.masters,
        allowed_salary_percentage: item.allowed_salary_percentage,
        work_payment_percentage: item.work_payment_percentage,
        light_green_bar_percentage: item.light_green_bar_percentage,
        file: item.user_file
          ? [
              {
                uid: item.user_file.id,
                name: item.user_file.file_name,
                status: "done",
                url: `${apiDomain}/userFiles/${item.user_file.file_path}`,
                size: item.user_file.size,
              },
            ]
          : null,
      });

      setStagesList(() =>
        createObjectStagesList.data.reduce<ObjectType["stages"]>(
          (result, stage) => {
            const stageFound = item.stages
              ? item.stages.find((itemState) => itemState.id === stage.id)
              : undefined;

            if (stageFound) {
              result.push({
                ...stageFound,
                isEnabled: stage.status === "active",
              });

              return result;
            }

            if (stage.status === "active") {
              result.push({
                ...stage,
                isEnabled: stage.status === "active",
                rate_code: "",
                short_name: "",
                status: "unactive",
                units: stage.units?.map((unit) => ({
                  ...unit,
                  value: null,
                })),
              });

              return result;
            }

            return result;
          },
          []
        )
      );

      setMeasures(() => {
        return item.measures;
      });

      setMasters(() => {
        return mastersList.data
          .filter((mastersListItem) =>
            (item.masters ?? [])
              .flatMap((master) => master.id)
              .includes(mastersListItem.id)
          )
          .map((mastersListItem) => {
            return {
              ...mastersListItem,
              status:
                (item.masters ?? []).find(
                  (master) => master.id === mastersListItem.id
                )?.status ?? "active",
            };
          });
      });
    }

    setIsError(false);
  }, [
    isEditMode,
    isAddMode,
    form,
    list.isLoaded,
    setIsError,
    setIsSubmittable,
    setStagesList,
    setMeasures,
    setMasters,
    createObjectStagesList,
    mastersList,
  ]);

  //  Add button sets add mode or reset it
  const handleAddButton = useCallback(() => {
    navigate(`/objects-and-clients/add`);
  }, [navigate]);

  const handleBackButton = useCallback(() => {
    navigate(`/objects-and-clients`);
  }, [navigate]);

  //  Tab name
  const tabName = useMemo(() => {
    if (!tab) return "";

    const tabsList = {
      default: "Общие данные",
      measures: "Замеры",
      masters: "Мастера",
      report: "Отчет по объекту",
    };

    return tabsList[tab as keyof typeof tabsList];
  }, [tab]);

  //  Tab value
  const tabValue = useMemo(() => {
    const tabsList = {
      default: "Общие данные",
      measures: "Замеры",
      masters: "Мастера",
      report: "Отчет по объекту",
    };

    return tab as keyof typeof tabsList;
  }, [tab]);

  //  Object from isEditMode and list
  const selectedObject = useMemo(() => {
    if (!list.isLoaded) return undefined;

    if (!list.data?.length) return undefined;

    return list.data.find((item) => item.id === isEditMode);
  }, [list, isEditMode]);

  const handleEditObjectStageRateCode = useCallback(
    ({ stageIndex, value }: { stageIndex: number; value: string }) => {
      setStagesList((stages) => {
        return stages.map((stage, index) => {
          if (index !== stageIndex) return stage;

          return { ...stage, rate_code: value };
        });
      });
    },
    [setStagesList]
  );

  //  Tab changed
  useEffect(() => {
    if (tabValue === "measures") return;

    setIsAddBuildingMode(false);
    setIsEditBuildingMode(undefined);

    setIsAddGroupMode(false);
    setIsEditGroupMode(undefined);

    setIsAddElementMode(false);
    setIsEditElementMode(undefined);
  }, [
    tabValue,
    setIsAddBuildingMode,
    setIsEditBuildingMode,
    setIsAddGroupMode,
    setIsEditGroupMode,
    setIsAddElementMode,
    setIsEditElementMode,
  ]);

  //  Tab changed
  useEffect(() => {
    if (tabValue === "masters") return;

    setIsAddMasterMode(false);
  }, [tabValue, setIsAddMasterMode]);

  const handleEditObjectStageShortName = useCallback(
    ({ stageIndex, value }: { stageIndex: number; value: string }) => {
      setStagesList((stages) => {
        return stages.map((stage, index) => {
          if (index !== stageIndex) return stage;

          return { ...stage, short_name: value };
        });
      });
    },
    [setStagesList]
  );

  const handleEditObjectStageStatus = useCallback(
    ({
      stageIndex,
      value,
    }: {
      stageIndex: number;
      value: "active" | "unactive";
    }) => {
      setStagesList((stages) => {
        return stages.map((stage, index) => {
          if (index !== stageIndex) return stage;

          return { ...stage, status: value };
        });
      });
    },
    [setStagesList]
  );

  const handleEditObjectStageUnitValue = useCallback(
    ({
      stageIndex,
      unitId,
      value,
    }: {
      stageIndex: number;
      unitId: number;
      value: number | null;
    }) => {
      setStagesList((stages) => {
        return stages.map((stage, index) => {
          if (index !== stageIndex) return stage;

          return {
            ...stage,
            units: stage.units?.map((unit) => {
              if (unit.id === unitId) return { ...unit, value };

              return unit;
            }),
          };
        });
      });
    },
    [setStagesList]
  );

  const handleDeleteBuilding = useCallback(
    (buildingId: string) => {
      Modal.confirm({
        title: "Вы уверены, что хотите удалить помещение?",
        content: "После удаления данные будут потеряны.",
        okText: "Да",
        cancelText: "Нет",
        onOk: () => {
          setMeasures((measures) => {
            return measures.filter(
              (measure) => measure.id.toString() !== buildingId
            );
          });
        },
      });
    },
    [setMeasures]
  );

  const handleDeleteGroup = useCallback(
    (groupId: string) => {
      Modal.confirm({
        title: "Вы уверены, что хотите удалить группу?",
        content: "После удаления данные будут потеряны.",
        okText: "Да",
        cancelText: "Нет",
        onOk: () => {
          setMeasures((measures) => {
            return measures.map((measure) => {
              if (!measure.groups) return measure;

              return {
                ...measure,
                groups: measure.groups.filter(
                  (measure) => measure.id.toString() !== groupId
                ),
              };
            });
          });
        },
      });
    },
    [setMeasures]
  );

  const handleDeleteElement = useCallback(
    (elementId: string) => {
      Modal.confirm({
        title: "Вы уверены, что хотите удалить элемент?",
        content: "После удаления данные будут потеряны.",
        okText: "Да",
        cancelText: "Нет",
        onOk: () => {
          setMeasures((measures) => {
            return measures.map((measure) => {
              if (!measure.groups) return measure;

              return {
                ...measure,
                groups: measure.groups.map((group) => {
                  if (!group.elements) return group;

                  return {
                    ...group,
                    elements: group.elements.filter(
                      (element) => element.id.toString() !== elementId
                    ),
                  };
                }),
              };
            });
          });
        },
      });
    },
    [setMeasures]
  );

  const handleCopyBuilding = useCallback(
    (buildingId: string) => {
      setMeasures((measures) => {
        const foundBuilding = measures.find(
          (building) => building.id.toString() === buildingId
        );

        if (!foundBuilding) return measures;

        const maxBuildingId = measures
          .filter((measure) =>
            measure.id.toString()?.startsWith("newBuilding-")
          )
          .map((measure) => parseInt(measure.id.toString().split("-")[1], 10))
          .reduce((max, current) => Math.max(max, current), 0);

        let maxGroupId = measures
          .flatMap((measure) => measure.groups || [])
          .filter((group) => group.id.toString()?.startsWith("newGroup-"))
          .map((group) => parseInt(group.id.toString().split("-")[1], 10))
          .reduce((max, current) => Math.max(max, current), 0);

        let maxElementId = measures
          .flatMap(
            (measure) =>
              measure.groups?.flatMap(
                (groupItem) => groupItem.elements || []
              ) || []
          )
          .filter((element) => element.id.toString()?.startsWith("newElement-"))
          .map((element) => parseInt(element.id.toString().split("-")[1], 10))
          .reduce((max, current) => Math.max(max, current), 0);

        return [
          ...measures,
          {
            ...foundBuilding,
            id: `newBuilding-${maxBuildingId + 1}`,
            groups: (foundBuilding.groups ? foundBuilding.groups : []).map(
              (group) => {
                maxGroupId++;

                return {
                  ...group,
                  id: `newGroup-${maxGroupId}`,
                  elements: (group.elements ? group.elements : []).map(
                    (element) => {
                      maxElementId++;

                      return {
                        ...element,
                        id: `newElement-${maxElementId}`,
                      };
                    }
                  ),
                };
              }
            ),
          },
        ];
      });
    },
    [setMeasures]
  );

  const handleCopyGroup = useCallback(
    (groupId: string) => {
      setMeasures((measures) => {
        const result = measures.reduce<{
          building?: MeasureType;
          group?: NonNullable<MeasureType["groups"]>[number];
        }>((result, building) => {
          const group = building.groups?.find(
            (group) => group.id.toString() === groupId
          );
          if (group) {
            result = { building, group };
          }
          return result;
        }, {});

        const foundBuilding = result.building;
        const foundGroup = result.group;

        if (!foundBuilding || !foundGroup) return measures;

        const maxGroupId = measures
          .flatMap((measure) => measure.groups || [])
          .filter((group) => group.id.toString()?.startsWith("newGroup-"))
          .map((group) => parseInt(group.id.toString().split("-")[1], 10))
          .reduce((max, current) => Math.max(max, current), 0);

        let maxElementId = measures
          .flatMap(
            (measure) =>
              measure.groups?.flatMap(
                (groupItem) => groupItem.elements || []
              ) || []
          )
          .filter((element) => element.id.toString()?.startsWith("newElement-"))
          .map((element) => parseInt(element.id.toString().split("-")[1], 10))
          .reduce((max, current) => Math.max(max, current), 0);

        return measures.map((building) => {
          if (foundBuilding.id.toString() === building.id.toString())
            return {
              ...building,
              groups: [
                ...(building.groups ? building.groups : []),
                {
                  ...foundGroup,
                  id: `newGroup-${maxGroupId + 1}`,
                  elements: (foundGroup.elements
                    ? foundGroup.elements
                    : []
                  ).map((element) => {
                    maxElementId++;

                    return {
                      ...element,
                      id: `newElement-${maxElementId}`,
                    };
                  }),
                },
              ],
            };

          return building;
        });
      });
    },
    [setMeasures]
  );

  const handleCopyElement = useCallback(
    (elementId: string) => {
      setMeasures((measures) => {
        const result = measures.reduce<{
          building?: MeasureType;
          group?: NonNullable<MeasureType["groups"]>[number];
          element?: NonNullable<
            NonNullable<MeasureType["groups"]>[number]["elements"]
          >[number];
        }>((result, building) => {
          if (result.element) return result;

          const group = building.groups?.find((group) =>
            group.elements?.some(
              (element) => element.id.toString() === elementId
            )
          );

          if (group) {
            const element = group.elements?.find(
              (element) => element.id.toString() === elementId
            );
            if (element) {
              return { building, group, element };
            }
          }

          return result;
        }, {});

        const foundBuilding = result.building;
        const foundGroup = result.group;
        const foundElement = result.element;

        if (!foundBuilding || !foundGroup || !foundElement) return measures;

        const maxElementId = measures
          .flatMap(
            (measure) =>
              measure.groups?.flatMap(
                (groupItem) => groupItem.elements || []
              ) || []
          )
          .filter((element) => element.id.toString()?.startsWith("newElement-"))
          .map((element) => parseInt(element.id.toString().split("-")[1], 10))
          .reduce((max, current) => Math.max(max, current), 0);

        return measures.map((building: MeasureType) => {
          if (foundBuilding.id === building.id)
            return {
              ...building,
              groups: [...(building.groups ? building.groups : [])].map(
                (group: NonNullable<MeasureType["groups"]>[number]) => {
                  if (group.id === foundGroup.id)
                    return {
                      ...group,
                      elements: [
                        ...(group.elements ? group.elements : []),
                        {
                          ...foundElement,
                          id: `newElement-${maxElementId + 1}`,
                        },
                      ],
                    };

                  return group;
                }
              ),
            };

          return building;
        });
      });
    },
    [setMeasures]
  );

  const handleDisplayImageBuilding = useCallback(
    (buildingId: string) => {
      const building = measures.find(
        (measure) => measure.id.toString() === buildingId
      );

      if (!building) return;

      if (!building.user_file) return;

      setPreviewFile({
        uid: building.user_file.id,
        size: building.user_file.size,
        name: building.user_file.file_name,
        fileName: building.user_file.file_name,
        url: `${apiDomain}/userFiles/${building.user_file.file_path}`,
        thumbUrl: `${apiDomain}/userFiles/${building.user_file.file_path}`,
      });
    },
    [measures, setPreviewFile]
  );

  const handleDisplayImageGroup = useCallback(
    (groupId: string) => {
      const group = measures.reduce<MeasureGroupType | undefined>(
        (result, measure) => {
          if (!measure?.groups) return result;

          const group = measure.groups.find(
            (group) => group.id.toString() === groupId
          );

          if (!group) return result;

          return group;
        },
        undefined
      );

      if (!group) return;

      if (!group.user_file) return;

      setPreviewFile({
        uid: group.user_file.id,
        size: group.user_file.size,
        name: group.user_file.file_name,
        fileName: group.user_file.file_name,
        url: `${apiDomain}/userFiles/${group.user_file.file_path}`,
        thumbUrl: `${apiDomain}/userFiles/${group.user_file.file_path}`,
      });
    },
    [measures, setPreviewFile]
  );

  const handleDisplayImageElement = useCallback(
    (elementId: string) => {
      const element = measures.reduce<MeasureElementType | undefined>(
        (result, measure) => {
          if (!measure?.groups) return result;

          const element = measure.groups.reduce<MeasureElementType | undefined>(
            (groupResult, group) => {
              if (!group.elements) return groupResult;

              const element = group.elements.find(
                (element) => element.id.toString() === elementId
              );

              if (!element) return groupResult;

              return element;
            },
            undefined
          );

          if (!element) return result;

          return element;
        },
        undefined
      );

      if (!element) return;

      if (!element.user_file) return;

      setPreviewFile({
        uid: element.user_file.id,
        size: element.user_file.size,
        name: element.user_file.file_name,
        fileName: element.user_file.file_name,
        url: `${apiDomain}/userFiles/${element.user_file.file_path}`,
        thumbUrl: `${apiDomain}/userFiles/${element.user_file.file_path}`,
      });
    },
    [measures, setPreviewFile]
  );

  const isInProcess = useMemo(() => {
    if (!!isAddMode && !!isPostInProcess) return true;

    if (!!isEditMode && !!putIds.includes(isEditMode)) return true;

    return false;
  }, [isAddMode, isPostInProcess, isEditMode, putIds]);

  //  Submit form, depends of mode
  const handleFinish = useCallback(() => {
    if (isInProcess) return;
    if (isFileUploading) return;

    const fetchData = async () => {
      setIsError(false);

      const editModeId = isEditMode;

      if (editModeId)
        setPutIds((state) => {
          return [...state, editModeId];
        });

      if (!editModeId) setIsPostInProcess(true);

      const url = editModeId
        ? `${apiDomain}/api/records/objects/${editModeId}`
        : `${apiDomain}/api/records/objects`;

      const fileId = values?.file?.[0]?.response?.[0]?.id
        ? values?.file?.[0]?.response[0].id
        : values?.file?.[0]?.uid && !isNaN(Number(values?.file?.[0].uid))
        ? Number(values?.file?.[0].uid)
        : null;

      const bodyData = {
        name: values.name.trim(),
        address: values.address.trim(),
        area: values.area,
        status: values.status,
        senior_masters: values.senior_masters,
        masters: masters.map((master) => {
          return {
            id: master.id,
            status: master.status,
          };
        }),
        allowed_salary_percentage: values.allowed_salary_percentage,
        work_payment_percentage: values.work_payment_percentage,
        light_green_bar_percentage: values.light_green_bar_percentage,
        stages: stagesList.map((stage) => {
          return {
            id: stage.id,
            rate_code: stage.rate_code,
            short_name: stage.short_name,
            status: stage.status,
            units: stage.units
              ? stage.units.map((unit) => {
                  return {
                    id: unit.id,
                    value: unit.value,
                  };
                })
              : [],
          };
        }),
        user_file_id: fileId,
        measures: measures,
      };

      const options: RequestInit = {
        method: editModeId ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bodyData),
        credentials: "include",
      };

      try {
        const response = await fetch(url, options);

        if (!response.ok) throw new Error();

        openNotificationWithSuccess({ message: "Сохранение завершено" });

        await getList(true);

        if (editModeId)
          setPutIds((state) => {
            return state.filter((id) => id !== editModeId);
          });

        if (!editModeId) setIsPostInProcess(false);

        if (!editModeId) {
          const responseData = await response.json();

          navigate(
            `/objects-and-clients/edit/${responseData}${
              tabValue ? `/${tabValue}` : ``
            }`
          );
        }
      } catch (error) {
        setIsError(true);

        openNotificationWithError({
          message: "Произошла ошибка при сохранении объекта",
        });

        if (editModeId)
          setPutIds((state) => {
            return state.filter((id) => id !== editModeId);
          });

        if (!editModeId) setIsPostInProcess(false);
      }
    };

    fetchData();
  }, [
    isEditMode,
    values,
    stagesList,
    isFileUploading,
    isInProcess,
    measures,
    masters,
    tabValue,
    navigate,
    setIsError,
    getList,
    setIsPostInProcess,
    setPutIds,
    openNotificationWithError,
    openNotificationWithSuccess,
  ]);

  const objectStageColumns = useMemo(() => {
    if (!isAddMode && !isEditMode) return [];

    const result: ColumnsType = [
      {
        title: "Номер ед. расценки",
        key: "rate_code",
        width: "20%",
        render: (text: any, untypedRecord: unknown, index) => {
          const record = untypedRecord as ObjectType["stages"][number];
          const styles = record.styles;

          return (
            <div className={clsx(!record.isEnabled && styles?.disabledRow)}>
              <Input
                size="large"
                name={`stages[${index}][rate_code]`}
                onChange={(e) =>
                  handleEditObjectStageRateCode({
                    stageIndex: index,
                    value: e.target.value,
                  })
                }
                value={record.rate_code}
                disabled={isInProcess}
              />
            </div>
          );
        },
      },
      {
        title: "Применяется на объекте",
        key: "status",
        width: "30%",
        render: (text: any, untypedRecord: unknown, index) => {
          const record = untypedRecord as ObjectType["stages"][number];
          const styles = record.styles;

          return (
            <div className={clsx(!record.isEnabled && styles?.disabledRow)}>
              <Checkbox
                name={`stages[${index}][status]`}
                key={index}
                onChange={(e) =>
                  handleEditObjectStageStatus({
                    stageIndex: index,
                    value: e.target.checked ? "active" : "unactive",
                  })
                }
                checked={record.status === "active" ? true : false}
                disabled={isInProcess}
              >
                Да
              </Checkbox>
            </div>
          );
        },
      },
      {
        title: "Наименование этапа",
        key: "name",
        width: "50%",
        render: (text: any, untypedRecord: unknown, index) => {
          const record = untypedRecord as ObjectType["stages"][number];
          const styles = record.styles;

          return (
            <div className={clsx(!record.isEnabled && styles?.disabledRow)}>
              <div className={styles?.nameTitle}>{record.name}</div>
              <Input
                placeholder="Краткое наименование"
                size="large"
                name={`stages[${index}][short_name]`}
                onChange={(e) =>
                  handleEditObjectStageShortName({
                    stageIndex: index,
                    value: e.target.value,
                  })
                }
                value={record.short_name}
                disabled={isInProcess}
              />
            </div>
          );
        },
      },
      {
        title: "Группа этапа",
        key: "group_name",
        width: "50%",
        render: (text: any, untypedRecord: unknown, index) => {
          const record = untypedRecord as ObjectType["stages"][number];
          const styles = record.styles;

          return (
            <div className={clsx(!record.isEnabled && styles?.disabledRow)}>
              {record.stage_group_name}
            </div>
          );
        },
      },
    ];

    if (stageUnitsList.isLoaded && stageUnitsList.data.length)
      result.push({
        title: "Цена за ед. измерения",
        key: `units`,
        width: "50%",
        render: (text: any, untypedRecord: unknown, index) => {
          const record = untypedRecord as ObjectType["stages"][number];
          const styles = record.styles;

          return (
            <div className={styles?.units}>
              {stageUnitsList.data
                .filter((unit) => {
                  return stagesListRef.current.some((stage) => {
                    return stage.units?.some(
                      (unitInStage) => unitInStage.id === unit.id
                    );
                  });
                })
                .map((unit, unitIndex) => {
                  const unitInStage = record.units?.find(
                    (unitInStage) => unitInStage.id === unit.id
                  );

                  if (!unitInStage) return null;

                  return (
                    <div
                      className={clsx(
                        styles?.unit,
                        !record.isEnabled && styles?.disabledRow
                      )}
                      key={unitIndex}
                    >
                      <div className={styles?.unitTitle}>{unit.name}</div>
                      <InputNumber
                        step={1}
                        min={0}
                        size="large"
                        name={`stages[${index}][unit][${unitInStage.id}]`}
                        onChange={(value) =>
                          handleEditObjectStageUnitValue({
                            stageIndex: index,
                            unitId: unitInStage.id,
                            value: value,
                          })
                        }
                        value={unitInStage.value}
                        disabled={isInProcess}
                      />
                    </div>
                  );
                })}
            </div>
          );
        },
      });

    return result;
  }, [
    handleEditObjectStageRateCode,
    handleEditObjectStageShortName,
    handleEditObjectStageStatus,
    handleEditObjectStageUnitValue,
    isInProcess,
    stageUnitsList,
    stagesListRef,
    isAddMode,
    isEditMode,
  ]);

  const measuresTableData = useMemo(() => {
    const result: MeasureTableElementType[] = [];

    measures.forEach((measure) => {
      const measureData = { ...measure, groups: undefined };

      const buildingUnits: MeasureTableElementType["units"] = [];
      const groups: MeasureTableElementType[] = [];

      if (measure?.groups) {
        measure.groups.forEach((group) => {
          const groupData = { ...group };

          const groupUnits: MeasureTableElementType["units"] = [];
          const elements: MeasureTableElementType[] = [];

          if (group?.elements) {
            group.elements.forEach((element) => {
              const elementData = { ...element };

              if (measure.is_children_visible)
                if (group.is_children_visible)
                  elements.push({
                    ...elementData,
                    type: "element",
                    index: `element-${elementData.id}`,
                    is_children_visible: false,
                  });

              if (elementData.units?.length) {
                elementData.units.forEach((unit) => {
                  const groupUnitIndex = groupUnits.findIndex((groupUnit) => {
                    return groupUnit.id === unit.id;
                  });

                  if (groupUnitIndex === -1) {
                    groupUnits.push({ ...unit });
                  }

                  if (groupUnitIndex !== -1) {
                    const groupUnitValue = groupUnits[groupUnitIndex].value;
                    const unitValue = unit.value;

                    groupUnits[groupUnitIndex].value = (
                      (groupUnitValue ? parseFloat(groupUnitValue) : 0) +
                      (unitValue ? parseFloat(unitValue) : 0)
                    )
                      .toFixed(2)
                      .toString();
                  }
                });
              }
            });
          }

          if (measure.is_children_visible)
            groups.push({
              ...groupData,
              type: "group",
              index: `group-${groupData.id}`,
              units: groupUnits?.length ? [...groupUnits] : undefined,
            });

          if (groupUnits?.length) {
            groupUnits.forEach((groupUnit) => {
              const buildingUnitIndex = buildingUnits.findIndex(
                (buildingUnit) => {
                  return buildingUnit.id === groupUnit.id;
                }
              );

              if (buildingUnitIndex === -1) {
                buildingUnits.push({ ...groupUnit });
              }

              if (buildingUnitIndex !== -1) {
                const buildingUnitValue =
                  buildingUnits[buildingUnitIndex].value;
                const unitValue = groupUnit.value;

                buildingUnits[buildingUnitIndex].value = (
                  (buildingUnitValue ? parseFloat(buildingUnitValue) : 0) +
                  (unitValue ? parseFloat(unitValue) : 0)
                )
                  .toFixed(2)
                  .toString();
              }
            });
          }

          groups.push(...elements);
        });
      }

      result.push({
        ...measureData,
        type: "building",
        index: `building-${measureData.id}`,
        units: buildingUnits?.length ? [...buildingUnits] : undefined,
      });

      result.push(...groups);
    });

    return result;
  }, [measures]);

  const aviableUnits = useMemo(() => {
    const result: StageUnitType[] = [];

    stagesList.forEach((stage) => {
      if (stage.status !== "active") return;

      stageUnitsList.data.forEach((stageUnit) => {
        stage.units?.forEach((unitInStage) => {
          if (unitInStage.id === stageUnit.id) {
            if (!result.find((resultItem) => resultItem.id === stageUnit.id))
              result.push(stageUnit);
          }
        });
      });
    });

    return result;
  }, [stagesList, stageUnitsList]);

  const handleChangeStageValue = useCallback(
    ({
      buildingId,
      groupId,
      elementId,
      stageId,
      value,
    }: {
      buildingId?: number | string;
      groupId?: number | string;
      elementId?: number | string;
      stageId: number;
      value: boolean;
    }) => {
      setMeasures((measures) => {
        return measures.map((building) => {
          const groups = building.groups ? building.groups : [];

          //  Если выбрано строение
          const isAllBuildingElementsChecked =
            buildingId && buildingId.toString() === building.id.toString()
              ? building.groups?.every((group) =>
                  (group.elements ? group.elements : []).every((element) => {
                    return stagesList
                      .filter((stageItem) => stageItem.status === "active")
                      .reduce((result, stageItem) => {
                        if (result) return true;

                        if (stageItem.id.toString() !== stageId.toString())
                          return result;

                        return element.stageValues
                          ? !!element.stageValues.find((stageValue) => {
                              if (
                                stageValue.stageId.toString() !==
                                stageItem.id.toString()
                              )
                                return false;

                              if (!stageValue.value) return false;

                              return true;
                            })
                          : false;
                      }, false);
                  })
                )
              : undefined;

          return {
            ...building,
            groups: groups.map((group) => {
              const elements = group.elements ? group.elements : [];

              //  Если выбрана группа
              const isAllGroupElementsChecked =
                groupId && groupId.toString() === group.id.toString()
                  ? elements.every((element) => {
                      return stagesList
                        .filter((stageItem) => stageItem.status === "active")
                        .reduce((result, stageItem) => {
                          if (result) return true;

                          if (stageItem.id.toString() !== stageId.toString())
                            return result;

                          return element.stageValues
                            ? !!element.stageValues.find((stageValue) => {
                                if (
                                  stageValue.stageId.toString() !==
                                  stageItem.id.toString()
                                )
                                  return false;

                                if (!stageValue.value) return false;

                                return true;
                              })
                            : false;
                        }, false);
                    })
                  : undefined;

              return {
                ...group,
                elements: elements.map((element) => {
                  const stageValues = stagesList
                    .filter((stageItem) => stageItem.status === "active")
                    .map((stageItem) => {
                      const stageItemValue = element.stageValues
                        ? !!element.stageValues.find((stageValue) => {
                            if (
                              stageValue.stageId.toString() !==
                              stageItem.id.toString()
                            )
                              return false;

                            if (!stageValue.value) return false;

                            return true;
                          })
                        : false;

                      //  Если выбран элемент
                      const changedValue =
                        elementId &&
                        elementId.toString() === element.id.toString() &&
                        stageId.toString() === stageItem.id.toString()
                          ? value
                          : groupId &&
                            groupId.toString() === group.id.toString() &&
                            stageId.toString() === stageItem.id.toString()
                          ? !isAllGroupElementsChecked
                          : buildingId &&
                            buildingId.toString() === building.id.toString() &&
                            stageId.toString() === stageItem.id.toString()
                          ? !isAllBuildingElementsChecked
                          : stageItemValue;

                      return {
                        stageId: stageItem.id,
                        value: changedValue,
                      };
                    });

                  return { ...element, stageValues };
                }),
              };
            }),
          };
        });
      });
    },
    [setMeasures, stagesList]
  );

  const handleChangeChildrenVisibility = useCallback(
    ({
      type,
      id,
    }: {
      type: "group" | "building" | "element";
      id: string | number;
    }) => {
      if (type === "element") return;

      setMeasures((measures) => {
        return measures.map((building) => {
          return {
            ...building,
            is_children_visible:
              type === "building" && id === building.id
                ? !building?.is_children_visible
                : !!building?.is_children_visible,
            groups: (building.groups ? building.groups : []).map((group) => {
              return {
                ...group,
                is_children_visible:
                  type === "group" && id === group.id
                    ? !group.is_children_visible
                    : !!group.is_children_visible,
              };
            }),
          };
        });
      });
    },
    [setMeasures]
  );

  const measuresColumns = useMemo(() => {
    const result: ColumnsType = [
      {
        title: "Наименование",
        key: "name",
        width: "20%",
        render: (text: any, untypedRecord: unknown, index) => {
          const record = untypedRecord as MeasureTableElementType;
          const styles = record.styles;

          const { isHasChildren } = {
            element: () => {
              return {
                isHasChildren: false,
              };
            },
            group: () => {
              const currentGroup = measures
                .flatMap((building) => building.groups || [])
                .find((group) => group.id.toString() === record.id.toString());

              const isHasChildren = !!currentGroup?.elements?.length;

              return {
                isHasChildren,
              };
            },
            building: () => {
              const currentBuilding = measures.find((building) => {
                return building.id.toString() === record.id.toString();
              });

              const isHasChildren = !!currentBuilding?.groups?.length;

              return { isHasChildren };
            },
            default: () => {
              return {
                isHasChildren: false,
              };
            },
          }[record.type || "default"]();

          const type = record.type;

          return (
            <Space
              size="small"
              className={clsx(
                styles?.objectName,
                !!(record.type === "group") && styles?.group,
                !!(record.type === "element") && styles?.element
              )}
              onClick={() =>
                handleChangeChildrenVisibility({
                  type: type,
                  id: record.id,
                })
              }
            >
              {!!isHasChildren ? (
                <>
                  {record.is_children_visible ? (
                    <MinusOutlined
                      className={clsx(
                        styles?.minus,
                        record.type === "element" && styles?.elementIcon
                      )}
                    />
                  ) : (
                    <PlusOutlined
                      className={clsx(
                        styles?.plus,
                        record.type === "element" && styles?.elementIcon
                      )}
                    />
                  )}
                </>
              ) : (
                <div
                  className={clsx(
                    styles?.empty,
                    record.type === "element" && styles?.elementIcon
                  )}
                ></div>
              )}
              {record.name}
            </Space>
          );
        },
      },
      {
        title: "",
        key: "controls",
        width: "20%",
        render: (text: any, untypedRecord: unknown, index) => {
          const record = untypedRecord as MeasureTableElementType;
          const styles = record.styles;

          return (
            <Space size="small" className={styles?.buttons}>
              <Button
                icon={<EditOutlined />}
                onClick={() =>
                  record.type === "group"
                    ? setIsEditGroupMode(record.id.toString())
                    : record.type === "element"
                    ? setIsEditElementMode(record.id.toString())
                    : setIsEditBuildingMode(record.id.toString())
                }
                disabled={isInProcess}
              />
              <Button
                icon={<CopyOutlined />}
                onClick={() =>
                  record.type === "group"
                    ? handleCopyGroup(record.id.toString())
                    : record.type === "element"
                    ? handleCopyElement(record.id.toString())
                    : handleCopyBuilding(record.id.toString())
                }
                disabled={isInProcess}
              />
              <Button
                icon={<DeleteOutlined />}
                onClick={() =>
                  record.type === "group"
                    ? handleDeleteGroup(record.id.toString())
                    : record.type === "element"
                    ? handleDeleteElement(record.id.toString())
                    : handleDeleteBuilding(record.id.toString())
                }
                disabled={isInProcess}
              />
              {!!record.user_file_id && (
                <Button
                  icon={<PictureOutlined />}
                  onClick={() =>
                    record.type === "group"
                      ? handleDisplayImageGroup(record.id.toString())
                      : record.type === "element"
                      ? handleDisplayImageElement(record.id.toString())
                      : handleDisplayImageBuilding(record.id.toString())
                  }
                  disabled={isInProcess}
                />
              )}
            </Space>
          );
        },
      },
      {
        title: "Комментарий",
        key: "comment",
        width: "20%",
        render: (text: any, untypedRecord: unknown, index) => {
          const record = untypedRecord as MeasureTableElementType;

          return (
            <div>
              <Space size="small">{record.comment}</Space>
            </div>
          );
        },
      },
    ];

    aviableUnits.forEach((aviableUnit) => {
      result.push({
        title: aviableUnit.name,
        key: `aviableUnit-${aviableUnit.id}`,
        width: "5%",
        align: "right",
        render: (text: any, untypedRecord: unknown, index) => {
          const record = untypedRecord as MeasureTableElementType;

          const unitObject = record.units
            ? record.units.find((unit) => unit.id === aviableUnit.id)
            : undefined;

          return (
            <div>
              <Space size="small">
                {unitObject?.value ? unitObject.value : ""}
              </Space>
            </div>
          );
        },
      });
    });

    stagesList.forEach((stage) => {
      if (stage.status === "unactive") return;

      result.push({
        title: stage.short_name ? stage.short_name : stage.name,
        key: `stage-${stage.id}`,
        width: "5%",
        align: "right",
        render: (text: any, untypedRecord: unknown, index) => {
          const record = untypedRecord as MeasureTableElementType;

          const { isChecked, isSemiChecked, isDisabled } = {
            element: () => {
              if (!record.stageValues)
                return {
                  isChecked: false,
                  isSemiChecked: false,
                  isDisabled: false,
                };

              const isChecked = !!record.stageValues.find((stageValue) => {
                if (stageValue.stageId.toString() !== stage.id.toString())
                  return false;

                if (!stageValue.value) return false;

                return true;
              });

              return {
                isChecked,
                isSemiChecked: false,
                isDisabled: false,
              };
            },
            group: () => {
              const currentGroup = measures
                .flatMap((building) => building.groups || [])
                .find((group) => group.id.toString() === record.id.toString());

              const isDisabled =
                !currentGroup || !currentGroup.elements?.length;

              const isChecked = measures.reduce((buildingResult, building) => {
                if (!building.groups?.length) return buildingResult;

                //  Поскольку проверяется только 1 group, если в результате был успех хоть раз, возвращаем true
                if (buildingResult) return true;

                return building.groups.reduce((groupResult, group) => {
                  if (group.id.toString() !== record.id.toString())
                    return groupResult;

                  if (!group.elements?.length) return groupResult;

                  //  Поскольку выше сравнение с record.id, проверен будет 1 group
                  return !!group.elements.every((element) => {
                    return !!stagesList
                      .filter((stageItem) => stageItem.status === "active")
                      .reduce((result, stageItem) => {
                        if (result) return true;

                        if (stageItem.id.toString() !== stage.id.toString())
                          return result;

                        return element.stageValues
                          ? !!element.stageValues.find((stageValue) => {
                              if (
                                stageValue.stageId.toString() !==
                                stageItem.id.toString()
                              )
                                return false;

                              if (!stageValue.value) return false;

                              return true;
                            })
                          : false;
                      }, false);
                  });
                }, false);
              }, false);

              const isSemiChecked = isChecked
                ? false
                : measures.reduce((buildingResult, building) => {
                    if (!building.groups?.length) return buildingResult;

                    //  Поскольку проверяется только 1 group, если в результате был успех хоть раз, возвращаем true
                    if (buildingResult) return true;

                    //  Поскольку выше сравнение с record.id, проверен будет 1 group
                    return building.groups.reduce((groupResult, group) => {
                      if (group.id.toString() !== record.id.toString())
                        return groupResult;

                      if (!group.elements?.length) return groupResult;

                      return !!group.elements.some((element) => {
                        return !!stagesList
                          .filter((stageItem) => stageItem.status === "active")
                          .reduce((result, stageItem) => {
                            if (result) return true;

                            if (stageItem.id.toString() !== stage.id.toString())
                              return result;

                            return element.stageValues
                              ? !!element.stageValues.find((stageValue) => {
                                  if (
                                    stageValue.stageId.toString() !==
                                    stageItem.id.toString()
                                  )
                                    return false;

                                  if (!stageValue.value) return false;

                                  return true;
                                })
                              : false;
                          }, false);
                      });
                    }, false);
                  }, false);

              return {
                isChecked,
                isSemiChecked,
                isDisabled,
              };
            },
            building: () => {
              const currentBuilding = measures.find((building) => {
                return building.id.toString() === record.id.toString();
              });

              const isDisabled =
                !currentBuilding?.groups?.length ||
                currentBuilding.groups.every((group) => {
                  return !group.elements?.length;
                });

              const isChecked = measures.reduce((buildingResult, building) => {
                if (!building.groups?.length) return buildingResult;

                if (building.id.toString() !== record.id.toString())
                  return buildingResult;

                if (!building.groups.some((group) => !!group.elements?.length))
                  return buildingResult;

                return building.groups.every((group) => {
                  if (!group.elements?.length) return true;

                  return !!group.elements.every((element) => {
                    return !!stagesList
                      .filter((stageItem) => stageItem.status === "active")
                      .reduce((result, stageItem) => {
                        if (result) return true;

                        if (stageItem.id.toString() !== stage.id.toString())
                          return result;

                        return element.stageValues
                          ? !!element.stageValues.find((stageValue) => {
                              if (
                                stageValue.stageId.toString() !==
                                stageItem.id.toString()
                              )
                                return false;

                              if (!stageValue.value) return false;

                              return true;
                            })
                          : false;
                      }, false);
                  });
                }, false);
              }, false);

              const isSemiChecked = isChecked
                ? false
                : measures.reduce((buildingResult, building) => {
                    if (!building.groups?.length) return buildingResult;

                    if (building.id.toString() !== record.id.toString())
                      return buildingResult;

                    if (
                      !building.groups.some((group) => !!group.elements?.length)
                    )
                      return buildingResult;

                    return building.groups.some((group) => {
                      if (!group.elements?.length) return false;

                      return !!group.elements.some((element) => {
                        return !!stagesList
                          .filter((stageItem) => stageItem.status === "active")
                          .reduce((result, stageItem) => {
                            if (result) return true;

                            if (stageItem.id.toString() !== stage.id.toString())
                              return result;

                            return element.stageValues
                              ? !!element.stageValues.find((stageValue) => {
                                  if (
                                    stageValue.stageId.toString() !==
                                    stageItem.id.toString()
                                  )
                                    return false;

                                  if (!stageValue.value) return false;

                                  return true;
                                })
                              : false;
                          }, false);
                      });
                    }, false);
                  }, false);

              return { isChecked, isSemiChecked, isDisabled };
            },
            default: () => {
              return {
                isChecked: false,
                isSemiChecked: false,
                isDisabled: false,
              };
            },
          }[record.type || "default"]();

          return (
            <div>
              <Space size="small">
                <Checkbox
                  onChange={(event) =>
                    handleChangeStageValue({
                      buildingId:
                        record.type === "building" ? record.id : undefined,
                      groupId: record.type === "group" ? record.id : undefined,
                      elementId:
                        record.type === "element" ? record.id : undefined,
                      stageId: stage.id,
                      value: event.target.checked,
                    })
                  }
                  checked={isChecked}
                  indeterminate={isSemiChecked}
                  disabled={isInProcess || isDisabled}
                />
              </Space>
            </div>
          );
        },
      });
    });

    return result;
  }, [
    isInProcess,
    aviableUnits,
    stagesList,
    measures,
    handleChangeStageValue,
    setIsEditBuildingMode,
    handleDeleteBuilding,
    handleDisplayImageBuilding,
    setIsEditGroupMode,
    handleDeleteGroup,
    handleDisplayImageGroup,
    setIsEditElementMode,
    handleDeleteElement,
    handleDisplayImageElement,
    handleChangeChildrenVisibility,
    handleCopyBuilding,
    handleCopyGroup,
    handleCopyElement,
  ]);

  const handleChangeMasterStatus = useCallback(
    (masterId: number) => {
      setMasters((masters) => {
        const result: MasterType[] = masters.map((master) => {
          if (master.id !== masterId) return master;

          return {
            ...master,
            status: master.status === "active" ? "unactive" : "active",
          };
        });

        return result;
      });
    },
    [setMasters]
  );

  const mastersTableData = useMemo(() => {
    const result: MasterType[] = masters;

    return result;
  }, [masters]);

  const mastersColumns = useMemo(() => {
    const result: ColumnsType = [
      {
        title: "ФИО мастера",
        key: "name",
        width: "20%",
        render: (text: any, untypedRecord: unknown, index) => {
          const record = untypedRecord as MasterType;
          const styles = record.styles;

          return (
            <div
              className={clsx(
                styles?.masters,
                record.status === "unactive" && styles?.disabled
              )}
            >
              <div className={styles?.name}>
                <Link
                  to={`/masters/edit/${record.id}`}
                  className={clsx(!!isInProcess && styles?.isInProcess)}
                >
                  {record.name}
                </Link>
              </div>
              <div className={styles?.rate}>
                Ставка в час: {record.hourly_rate.toLocaleString("ru-RU")} ₽
              </div>
              <div className={styles?.status}>
                Статус:{" "}
                {record.status === "active" ? "в проекте" : "удален из проекта"}
              </div>
              <div className={styles?.button}>
                <Button
                  onClick={() => handleChangeMasterStatus(record.id)}
                  type={"primary"}
                  disabled={isInProcess}
                >
                  {record.status === "active"
                    ? "Удалить из проекта"
                    : "Вернуть в проект"}
                </Button>
              </div>
            </div>
          );
        },
      },
    ];

    return result;
  }, [isInProcess, handleChangeMasterStatus]);

  const statusesList = useMemo(() => {
    return {
      calculation: "Расчет",
      in_progress: "В работе",
      cancelled: "Отменен",
      paused: "Пауза",
      completed: "Сдан",
      client_refused: "Отказ клиента",
    };
  }, []);

  //  Set columns for table
  const columns: ColumnsType = useMemo(() => {
    const result: ColumnsType = [
      {
        title: "id",
        key: "id",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as ObjectType;

          return <div>{record.id}</div>;
        },
      },
      {
        title: "Название",
        key: "name",
        width: "20%",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as ObjectType;

          return <div>{record.name}</div>;
        },
      },
      {
        title: "Адрес",
        key: "address",
        width: "20%",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as ObjectType;

          return <div>{record.address}</div>;
        },
      },
      {
        title: "Предв. площадь",
        key: "area",
        width: "20%",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as ObjectType;

          return <div>{record.area}</div>;
        },
      },
      {
        title: "Ст. мастера",
        key: "senior_masters",
        width: "20%",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as ObjectType;

          return (
            <div>
              {record.senior_masters
                ?.map((seniorMasterId) => {
                  const currentMaster = seniorMastersList.data.find(
                    (seniorMaster) => seniorMaster.id === seniorMasterId
                  );

                  if (!currentMaster) return null;

                  return currentMaster.name;
                })
                .join(", ")}
            </div>
          );
        },
      },
      {
        title: "Статус",
        key: "status",
        width: "20%",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as ObjectType;

          return <div>{statusesList[record.status] || ""}</div>;
        },
      },
      {
        key: "edit",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as ObjectType;

          return (
            <>
              <Button
                onClick={() => handleEditButton(record.id)}
                type={"primary"}
                disabled={isInProcess}
              >
                Изменить
              </Button>
            </>
          );
        },
      },
    ];

    return result;
  }, [handleEditButton, isInProcess, statusesList, seniorMastersList]);

  const handleUploadFileChange = useCallback(
    (info: UploadChangeParam) => {
      setIsFileUploading(info.file.status === "uploading");
    },
    [setIsFileUploading]
  );

  const handlePreview = useCallback(
    async (previewFile: UploadFile) => {
      setPreviewFile(previewFile);
    },
    [setPreviewFile]
  );

  return (
    <>
      <MobileInnerWrapper>
        <Row justify="space-between" align="top" className={styles.titleBlock}>
          <Col>
            <Title level={3} className={styles.title}>
              {isAddBuildingMode
                ? `Добавление помещения`
                : isEditBuildingMode
                ? `Редактирование помещения`
                : isAddGroupMode
                ? `Добавление группы`
                : isEditGroupMode
                ? `Редактирование группы`
                : isAddElementMode
                ? `Добавление элемента`
                : isEditElementMode
                ? `Редактирование элемента`
                : isAddMasterMode
                ? `Добавление мастера`
                : isAddMode
                ? `Добавление объекта`
                : isEditMode
                ? tabValue === "report"
                  ? tabName
                  : `Редактирование объекта №${isEditMode}`
                : `Объекты`}
            </Title>
            {!!(
              tabValue !== "report" &&
              (isAddMode || isEditMode) &&
              !isAddBuildingMode &&
              !isEditBuildingMode &&
              !isAddGroupMode &&
              !isEditGroupMode &&
              !isAddElementMode &&
              !isEditElementMode &&
              !isAddMasterMode
            ) && (
              <Title level={4} className={styles.subTitle}>
                {tabName}
              </Title>
            )}
          </Col>
          <Col>
            <Button
              type="primary"
              onClick={
                isAddMode || isEditMode
                  ? isAddBuildingMode
                    ? () => setIsAddBuildingMode(false)
                    : isEditBuildingMode
                    ? () => setIsEditBuildingMode(undefined)
                    : isAddGroupMode
                    ? () => setIsAddGroupMode(false)
                    : isEditGroupMode
                    ? () => setIsEditGroupMode(undefined)
                    : isAddElementMode
                    ? () => setIsAddElementMode(false)
                    : isEditElementMode
                    ? () => setIsEditElementMode(undefined)
                    : isAddMasterMode
                    ? () => setIsAddMasterMode(false)
                    : handleBackButton
                  : handleAddButton
              }
              disabled={isInProcess}
            >
              {isAddMode || isEditMode
                ? isAddBuildingMode ||
                  isEditBuildingMode ||
                  isAddGroupMode ||
                  isEditGroupMode ||
                  isAddElementMode ||
                  isEditElementMode
                  ? `Вернуться к замерам`
                  : isAddMasterMode
                  ? "Вернуться к списку мастеров"
                  : `Вернуться к списку`
                : `Добавить объект`}
            </Button>
          </Col>
        </Row>
        {tabValue === "report" && !!selectedObject && (
          <Report selectedObject={selectedObject} />
        )}
        {tabValue !== "report" && (
          <>
            {(!!isAddMode || !!isEditMode) &&
              ((!!seniorMastersList.isLoaded &&
                !seniorMastersList.data.length) ||
                (!!mastersList.isLoaded && !mastersList.data.length) ||
                (!!stageUnitsList.isLoaded && !stageUnitsList.data.length) ||
                (!!createObjectStagesList.isLoaded &&
                  !createObjectStagesList.data.length) ||
                (!!surfacesList.isLoaded && !surfacesList.data.length)) && (
                <Typography>
                  <Text type="danger">
                    Для {isEditMode ? "редактирования" : "создания"} объекта
                    необходимо добавить старшего мастера, мастера в список
                    мастеров и добавить единицы измерения.
                  </Text>
                </Typography>
              )}
            {(!!isAddMode || !!isEditMode) &&
              !!seniorMastersList.data.length &&
              !!mastersList.data.length &&
              !!stageUnitsList.data.length &&
              !!createObjectStagesList.data.length &&
              !!surfacesList.data.length && (
                <>
                  <Flex
                    vertical
                    gap={16}
                    className={clsx(
                      !!(
                        !!isAddBuildingMode ||
                        !!isEditBuildingMode ||
                        !!isAddGroupMode ||
                        !!isEditGroupMode ||
                        !!isAddElementMode ||
                        !!isEditElementMode ||
                        !!isAddMasterMode
                      ) && styles.isHidden
                    )}
                  >
                    <Form
                      layout={"vertical"}
                      form={form}
                      onFinish={handleFinish}
                      initialValues={{
                        name: "",
                        address: "",
                        area: 0,
                        status: "calculation",
                        senior_masters: [],
                        masters: [],
                        allowed_salary_percentage: 0,
                        work_payment_percentage: 0,
                        light_green_bar_percentage: 0,
                        file: null,
                      }}
                    >
                      <div
                        className={clsx(
                          tabValue !== "default" && styles.isHidden
                        )}
                      >
                        <Form.Item
                          name="name"
                          label="Название"
                          rules={[
                            {
                              required: true,
                              message: "Пожалуйста, введите название!",
                            },
                          ]}
                          validateTrigger={false}
                          required={false}
                        >
                          <Input
                            size="large"
                            name="name"
                            disabled={isInProcess || !list.isLoaded}
                          />
                        </Form.Item>
                        <Form.Item
                          name="address"
                          label="Адрес"
                          validateTrigger={false}
                          required={false}
                        >
                          <Input
                            size="large"
                            name="name"
                            disabled={isInProcess || !list.isLoaded}
                          />
                        </Form.Item>
                        <Form.Item
                          name="area"
                          label={
                            <>
                              Предварительная площадь объекта по полу, м
                              <sup>2</sup>
                            </>
                          }
                          validateTrigger={false}
                          required={false}
                        >
                          <InputNumber
                            size="large"
                            step={1}
                            min={0}
                            name="area"
                            disabled={isInProcess || !list.isLoaded}
                          />
                        </Form.Item>
                        <Form.Item
                          name="status"
                          label="Статус"
                          rules={[
                            {
                              required: true,
                              message: "Пожалуйста, выберите статус!",
                            },
                          ]}
                          validateTrigger={false}
                          required={false}
                        >
                          <Select
                            placeholder="Выберите статус"
                            disabled={isInProcess || !list.isLoaded}
                          >
                            {Object.entries(statusesList).map(
                              ([statusKey, statusLabel]) => (
                                <Select.Option
                                  key={statusKey}
                                  value={statusKey}
                                >
                                  {statusLabel}
                                </Select.Option>
                              )
                            )}
                          </Select>
                        </Form.Item>
                        <Form.Item
                          name="senior_masters"
                          label="Старшие мастера объекта"
                          validateTrigger={false}
                          required={false}
                        >
                          <Select
                            mode="multiple"
                            placeholder="Выберите старшего мастера"
                            disabled={isInProcess || !list.isLoaded}
                          >
                            {seniorMastersList.data.map((seniorMaster) => (
                              <Select.Option
                                key={seniorMaster.id}
                                value={seniorMaster.id}
                              >
                                {seniorMaster.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Form.Item
                          name="work_payment_percentage"
                          label="% оплаты за работу"
                          rules={[
                            {
                              required: false,
                              message: "Пожалуйста, введите %!",
                            },
                          ]}
                          validateTrigger={false}
                          required={false}
                        >
                          <InputNumber
                            size="large"
                            step={1}
                            min={0}
                            max={100}
                            name="work_payment_percentage"
                            disabled={isInProcess || !list.isLoaded}
                          />
                        </Form.Item>
                        <Form.Item
                          name="light_green_bar_percentage"
                          label="% светло-зеленой полосы"
                          rules={[
                            {
                              required: false,
                              message: "Пожалуйста, введите %!",
                            },
                          ]}
                          validateTrigger={false}
                          required={false}
                        >
                          <InputNumber
                            size="large"
                            step={1}
                            min={0}
                            max={100}
                            name="light_green_bar_percentage"
                            disabled={isInProcess || !list.isLoaded}
                          />
                        </Form.Item>
                        <Form.Item
                          name="allowed_salary_percentage"
                          label="Разрешенный % к выплате в качестве зп"
                          rules={[
                            {
                              required: false,
                              message: "Пожалуйста, введите %!",
                            },
                          ]}
                          validateTrigger={false}
                          required={false}
                        >
                          <InputNumber
                            size="large"
                            step={1}
                            min={0}
                            max={100}
                            name="allowed_salary_percentage"
                            disabled={isInProcess || !list.isLoaded}
                          />
                        </Form.Item>
                        <Title level={4} className={styles.subTitle}>
                          Единичные расценки
                        </Title>
                        <Table
                          dataSource={stagesList}
                          columns={objectStageColumns}
                          rowKey="id"
                          pagination={false}
                          className={styles.table}
                          rowClassName={styles.tableRow}
                          styles={styles}
                        />
                        <Form.Item
                          label="Добавить фото или чертеж"
                          name="file"
                          valuePropName="fileList"
                          getValueFromEvent={(e) => e.fileList}
                        >
                          <Upload
                            action={`${apiDomain}/api/upload-file`}
                            accept="image/*"
                            maxCount={1}
                            withCredentials={true}
                            disabled={isInProcess || !list.isLoaded}
                            onChange={handleUploadFileChange}
                            onPreview={handlePreview}
                            listType="picture-card"
                          >
                            <Button
                              icon={<UploadOutlined />}
                              disabled={
                                isInProcess || isFileUploading || !list.isLoaded
                              }
                            >
                              {isFileUploading
                                ? `Идет загрузка`
                                : `Загрузить файл`}
                            </Button>
                          </Upload>
                        </Form.Item>
                      </div>
                      <div
                        className={clsx(
                          tabValue !== "measures" && styles.isHidden
                        )}
                      >
                        <Table
                          dataSource={measuresTableData}
                          columns={measuresColumns}
                          rowKey="index"
                          pagination={false}
                          className={clsx(styles.table, styles.fullWidth)}
                          rowClassName={styles.tableRow}
                          styles={styles}
                          //scroll={{ x: 'max-content' }}
                        />
                        <Form.Item>
                          <Button
                            type="primary"
                            size="large"
                            disabled={isInProcess || !list.isLoaded}
                            onClick={() => setIsAddBuildingMode(true)}
                          >
                            Добавить помещение
                          </Button>
                          {!!measures.length && (
                            <Button
                              type="primary"
                              size="large"
                              disabled={isInProcess || !list.isLoaded}
                              onClick={() => setIsAddGroupMode(true)}
                              className={styles.leftMargin}
                            >
                              Добавить группу
                            </Button>
                          )}
                          {!!measures.filter(
                            (measure) => !!measure.groups?.length
                          ).length && (
                            <Button
                              type="primary"
                              size="large"
                              disabled={isInProcess || !list.isLoaded}
                              onClick={() => setIsAddElementMode(true)}
                              className={styles.leftMargin}
                            >
                              Добавить элемент
                            </Button>
                          )}
                        </Form.Item>
                      </div>
                      <div
                        className={clsx(
                          tabValue !== "masters" && styles.isHidden
                        )}
                      >
                        <Table
                          dataSource={mastersTableData}
                          columns={mastersColumns}
                          rowKey="id"
                          pagination={false}
                          className={styles.table}
                          rowClassName={styles.tableRow}
                          styles={styles}
                        />
                        <Form.Item>
                          <Button
                            type="primary"
                            size="large"
                            disabled={isInProcess || !list.isLoaded}
                            onClick={() => setIsAddMasterMode(true)}
                          >
                            Добавить мастера
                          </Button>
                        </Form.Item>
                      </div>
                      <Form.Item>
                        <Button
                          type="primary"
                          size="large"
                          className={styles.button}
                          disabled={!isSubmittable || isFileUploading}
                          htmlType="submit"
                          loading={isInProcess || !list.isLoaded}
                        >
                          Сохранить
                        </Button>
                      </Form.Item>
                      {!!isError && (
                        <Form.Item>
                          <Text type="danger">Не удалось сохранить объект</Text>
                        </Form.Item>
                      )}
                    </Form>
                  </Flex>
                  {!!(!!isAddBuildingMode || !!isEditBuildingMode) && (
                    <AddBuilding
                      measures={measures}
                      setMeasures={setMeasures}
                      setIsAddBuildingMode={setIsAddBuildingMode}
                      setIsEditBuildingMode={setIsEditBuildingMode}
                      isEditBuildingMode={isEditBuildingMode}
                      isAddBuildingMode={isAddBuildingMode}
                    />
                  )}
                  {!!(!!isAddGroupMode || !!isEditGroupMode) && (
                    <AddGroup
                      measures={measures}
                      setMeasures={setMeasures}
                      setIsAddGroupMode={setIsAddGroupMode}
                      setIsEditGroupMode={setIsEditGroupMode}
                      isEditGroupMode={isEditGroupMode}
                      isAddGroupMode={isAddGroupMode}
                    />
                  )}
                  {!!(!!isAddElementMode || !!isEditElementMode) && (
                    <AddElement
                      stageUnitsList={stageUnitsList}
                      stagesList={stagesList}
                      surfacesList={surfacesList}
                      measures={measures}
                      setMeasures={setMeasures}
                      setIsAddElementMode={setIsAddElementMode}
                      setIsEditElementMode={setIsEditElementMode}
                      isEditElementMode={isEditElementMode}
                      isAddElementMode={isAddElementMode}
                    />
                  )}
                  {!!!!isAddMasterMode && (
                    <AddMaster
                      masters={masters}
                      mastersList={mastersList}
                      setIsAddMasterMode={setIsAddMasterMode}
                      setMasters={setMasters}
                    />
                  )}
                </>
              )}
          </>
        )}
        {!isAddMode && !isEditMode && (
          <>
            <Card
              className={styles.filters}
              classNames={{
                body: isMobile ? styles.mobileFiltersBody : undefined,
              }}
            >
              <Input
                placeholder="Фильтр по имени"
                value={nameFilter}
                onChange={(event) => setNameFilter(event.target.value)}
                className={clsx(
                  styles.nameFilter,
                  !!isMobile && styles.mobileNameFilter
                )}
                disabled={isInProcess}
              />
            </Card>
            <Table
              dataSource={list.data.filter((listItem) => {
                if (nameFilter)
                  if (
                    listItem.name &&
                    !listItem.name
                      .toLowerCase()
                      .includes(nameFilter.toLowerCase())
                  )
                    return false;

                return true;
              })}
              columns={columns}
              rowKey="id"
              pagination={false}
              loading={!list.isLoaded}
              styles={styles}
            />
          </>
        )}
      </MobileInnerWrapper>
      {isMobile && (
        <MobileModal
          visible={!!previewFile?.url || !!previewFile?.thumbUrl}
          content={
            <img
              key={
                previewFile?.url
                  ? previewFile?.url
                  : `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                  ? `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                  : previewFile?.thumbUrl
              }
              alt={previewFile?.fileName}
              style={{ width: "100%" }}
              src={
                previewFile?.url
                  ? previewFile?.url
                  : `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                  ? `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                  : previewFile?.thumbUrl
              }
              onClick={() => setPreviewFile(undefined)}
            />
          }
          closeOnMaskClick={true}
          showCloseButton={true}
          onClose={() => setPreviewFile(undefined)}
        />
      )}
      {!isMobile && (
        <Modal
          open={!!previewFile?.url || !!previewFile?.thumbUrl}
          title={previewFile?.fileName}
          footer={null}
          onCancel={() => setPreviewFile(undefined)}
        >
          <img
            key={
              previewFile?.url
                ? previewFile?.url
                : `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                ? `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                : previewFile?.thumbUrl
            }
            alt={previewFile?.fileName}
            style={{ width: "100%" }}
            src={
              previewFile?.url
                ? previewFile?.url
                : `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                ? `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                : previewFile?.thumbUrl
            }
            onClick={() => setPreviewFile(undefined)}
          />
        </Modal>
      )}
    </>
  );
};

export default Page;
